import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useUserRole } from "./useUserRole";

export type PermissionLevel = "none" | "view" | "edit" | "admin";

/**
 * Single source of truth for every module that participates in the role+user
 * permission system. The Admin permission matrix, the per-user override matrix,
 * and the role-default seed migration must all iterate over this list.
 *
 * Keep alphabetised by display category — adding a module here makes it appear
 * in the admin UI automatically.
 */
export const ALL_MODULES = [
  "dashboard",
  "crm",
  "npd",
  "stock_items",
  "label_proofing",
  "quality",
  "documents",
  "equipment",
  "suppliers",
  "contractors",
  "forms",
  "training",
  "settings",
  "warehouse",
  "manufacturing",
  "contacts",
  "planning",
  "purchasing",
  "consumables",
  "marketing",
  "timesheets",
  "hr",
] as const;

export type ModuleName = (typeof ALL_MODULES)[number];

/** Permission levels admins can pick in the matrix UI (excludes legacy 'read'/'write'/'full' aliases). */
export const ASSIGNABLE_PERMISSION_LEVELS: PermissionLevel[] = [
  "none",
  "view",
  "edit",
  "admin",
];

interface ModulePermission {
  module: string;
  permission: string;
}

const PERMISSION_PRECEDENCE: PermissionLevel[] = ["admin", "edit", "view", "none"];

/**
 * Modules that must NEVER be granted by the roles-empty "view" fallback below.
 * A just-provisioned / roles-less account should not even see sensitive surfaces
 * (e.g. HR employee data) advertised in the nav. The real gate (useModuleAccess +
 * RLS) already fails closed; this stops the nav from disclosing the module exists.
 */
const SENSITIVE_MODULES_NO_FALLBACK = new Set<string>(["hr"]);

export function normalizePermissionLevel(permission: string | null | undefined): PermissionLevel {
  switch (permission) {
    case "admin":
    case "full":
      return "admin";
    case "edit":
    case "write":
      return "edit";
    case "view":
    case "read":
      return "view";
    default:
      return "none";
  }
}

export function highestPermissionLevel(
  permissions: Array<string | null | undefined>,
): PermissionLevel {
  const normalized = permissions.map(normalizePermissionLevel);
  for (const level of PERMISSION_PRECEDENCE) {
    if (normalized.includes(level)) return level;
  }
  return "none";
}

/**
 * Fetch the current user's per-user module overrides, degrading to an empty list
 * (role-based access only) when the override layer is unavailable.
 *
 * Why fail-open here: `user_module_permissions` is a sparse, OPTIONAL layer on top of
 * role-based `module_permissions`. A failure reading it — table missing during a schema
 * drift, transient RLS/network error — must NEVER harden into a total access lockout,
 * because this query sits inside the global access gate every route waits on. (A missing
 * table returns PostgREST 404; left to throw, React Query retry-storms and `isLoading`
 * never settles → app-wide "Checking permissions…" hang. Regression of 2026-06-01.)
 *
 * Tradeoff: a user whose access was REVOKED via an override (permission below their role
 * default) regains role-level access while the override layer is down. Accepted risk —
 * revoke-overrides are rare and an outage-only window beats locking out the entire app.
 * The role layer (`module_permissions`) is deliberately NOT degraded by callers: a failure
 * there still throws, because it is the established bedrock table and silently failing it
 * open would compromise the whole permission model.
 */
export async function fetchUserModuleOverrides(userId: string): Promise<ModulePermission[]> {
  const { data, error } = await supabase
    .from("user_module_permissions")
    .select("module, permission")
    .eq("user_id", userId);

  if (error) {
    console.warn(
      `[useModuleAccess] user_module_permissions unavailable (${error.code ?? "unknown"}: ${error.message}); ` +
        "falling back to role-based permissions — per-user overrides are not applied until this recovers.",
    );
    return [];
  }

  return (data ?? []) as ModulePermission[];
}

export function useModuleAccess(module: ModuleName) {
  const { user } = useAuth();
  const { roles, isLoading: rolesLoading, isError: rolesError } = useUserRole();

  const { data, isLoading: permsLoading, isError: permsError } = useQuery({
    queryKey: ["module-permissions", user?.id, roles],
    queryFn: async () => {
      if (!user) return { rolePermissions: [], overrides: [] };

      const [roleRes, overrides] = await Promise.all([
        roles.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from("module_permissions")
              .select("module, permission")
              .in("role", roles as string[]),
        // Optional override layer — degrades to [] instead of bricking the gate.
        fetchUserModuleOverrides(user.id),
      ]);

      if (roleRes.error) throw roleRes.error;
      return {
        rolePermissions: (roleRes.data ?? []) as ModulePermission[],
        overrides,
      };
    },
    // Must NOT run until roles are known. The queryFn short-circuits on
    // roles.length === 0, so an unsettled roles array would let this query
    // resolve INSTANTLY with rolePermissions: [] — a settled-looking "none".
    enabled: !!user && !rolesLoading && !rolesError,
  });

  // Get the highest permission level for this module across all user roles
  const getPermission = (): PermissionLevel => {
    // Admin accounts always have full access, even if a specific module row
    // is temporarily missing from module_permissions.
    if (roles.includes("admin")) return "admin";

    if (!data) return "none";

    const override = data.overrides.find((p) => p.module === module);
    if (override) return normalizePermissionLevel(override.permission);

    const modulePerms = data.rolePermissions.filter((p) => p.module === module);
    return highestPermissionLevel(modulePerms.map((p) => p.permission));
  };

  const permission = getPermission();

  // A disabled query reports isLoading: false in React Query v5, so the roles
  // query's own loading state MUST be OR-ed in. Without this the gate reports a
  // settled verdict of "none" while roles are still in flight -> Access Denied
  // flashed at a full admin.
  const isLoading = rolesLoading || permsLoading;

  return {
    permission,
    canView: permission !== "none",
    canEdit: permission === "edit" || permission === "admin",
    isAdmin: permission === "admin",
    isLoading,
    /**
     * The permission verdict could not be established. "Unknown", NOT "denied" —
     * callers must distinguish.
     *
     * BOTH layers count. getPermission() returns "none" on `!data`, and `data` is
     * undefined when the module_permissions read THROWS (it does: `if
     * (roleRes.error) throw roleRes.error`) just as surely as when roles fail. So
     * reporting only `rolesError` left a transient network/RLS failure on the
     * bedrock table rendering as Access Denied with no Retry — the exact defect
     * this hook was changed to remove, surviving one layer down. The optional
     * override layer is deliberately excluded: fetchUserModuleOverrides fails OPEN
     * and returns [], so it never reaches this flag.
     */
    isError: rolesError || permsError,
  };
}

// Hook to get all module permissions for the current user
export function useAllModulePermissions() {
  const { user } = useAuth();
  const { roles, isLoading: rolesLoading, isError: rolesError } = useUserRole();
  const allModules = ALL_MODULES;

  const { data: permissions, isLoading: permsLoading, isError: permsError } = useQuery({
    queryKey: ["all-module-permissions", user?.id, roles],
    queryFn: async () => {
      if (!user) return {};

      // Admin role is a global override.
      if (roles.includes("admin")) {
        const adminMap: Record<string, PermissionLevel> = {};
        allModules.forEach((m) => {
          adminMap[m] = "admin";
        });
        return adminMap;
      }

      const [roleRes, overrides] = await Promise.all([
        roles.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from("module_permissions")
              .select("module, permission")
              .in("role", roles as string[]),
        // Optional override layer — degrades to [] instead of bricking the gate.
        fetchUserModuleOverrides(user.id),
      ]);

      if (roleRes.error) throw roleRes.error;

      // Build a map of module -> highest permission
      const permMap: Record<string, PermissionLevel> = {};

      for (const module of allModules) {
        const rolePerms = ((roleRes.data ?? []) as ModulePermission[])
          .filter((perm) => perm.module === module)
          .map((perm) => perm.permission);
        // View-only fallback mirrors the previous behavior when roles are absent,
        // while still allowing explicit per-user overrides to deny/grant access.
        // Sensitive modules (e.g. hr) are excluded so a roles-less account never has
        // them advertised in the nav.
        const rolesEmptyFallback: PermissionLevel = SENSITIVE_MODULES_NO_FALLBACK.has(module)
          ? "none"
          : "view";
        permMap[module] = roles.length === 0 ? rolesEmptyFallback : highestPermissionLevel(rolePerms);
      }

      for (const override of overrides) {
        permMap[override.module] = normalizePermissionLevel(override.permission);
      }

      return permMap;
    },
    // Same hazard as useModuleAccess: the roles-empty branch below resolves
    // instantly, and here it resolves to the "view" FALLBACK — so running it on
    // an unsettled roles array briefly advertises every module in the nav.
    enabled: !rolesLoading && !rolesError,
  });

  return {
    permissions: permissions ?? {},
    isLoading: rolesLoading || permsLoading,
    // Same reasoning as useModuleAccess: a failed module_permissions read yields
    // `permissions: {}` — indistinguishable from "this user has no modules" — so a
    // caller told isError: false silently hides every module from the nav.
    isError: rolesError || permsError,
  };
}
